import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AdminKeyGate } from "@/components/admin/AdminKeyGate";
import { SummaryCards } from "@/components/admin/SummaryCards";
import { KeywordTable } from "@/components/admin/KeywordTable";
import { LeadsTable } from "@/components/admin/LeadsTable";
import { LeadsFilters } from "@/components/admin/LeadsFilters";
import { PagePerfTable } from "@/components/admin/PagePerfTable";
import { formatDate } from "@/components/admin/StatusBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "admin_key";

const useNoIndex = () => {
  useEffect(() => {
    const meta = document.querySelector('meta[name="robots"]');
    const previous = meta?.getAttribute("content");
    meta?.setAttribute("content", "noindex, nofollow");
    document.title = "Leads & verdicts | Admin";
    return () => { if (previous) meta.setAttribute("content", previous); };
  }, []);
};

const matches = (r, f) => {
  const q = f.q.trim().toLowerCase();
  return (f.status === "all" || (r.status || "pending") === f.status)
    && (f.type === "all" || r.type === f.type)
    && (f.keyword === null || (r.keyword || "(sans mot-clé)") === f.keyword)
    && (!q || [r.name, r.phone, r.email, r.keyword].some((v) => (v || "").toLowerCase().includes(q)));
};

export default function AdminLeads() {
  useNoIndex();
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem(KEY) || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ days: "90", status: "all", type: "all", q: "", tests: false, keyword: null });
  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));

  const load = useCallback(async (key, days) => {
    setLoading(true);
    try {
      const opts = { params: { days }, headers: { "X-Admin-Key": key } };
      const [res, pages] = await Promise.all([axios.get(`${API}/admin/leads`, opts), axios.get(`${API}/admin/pages`, opts)]);
      sessionStorage.setItem(KEY, key);
      setAdminKey(key);
      setData({ ...res.data, pages: pages.data });
      setError("");
    } catch (e) {
      sessionStorage.removeItem(KEY);
      setAdminKey("");
      setData(null);
      setError(e.response?.status === 429 ? "Trop de tentatives. Réessayez dans 15 minutes." : "Clé admin incorrecte.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (adminKey) load(adminKey, f.days); }, [f.days]); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => { sessionStorage.removeItem(KEY); setAdminKey(""); setData(null); };

  if (!adminKey || !data) {
    return <AdminKeyGate onSubmit={(k) => load(k, f.days)} error={error} loading={loading} />;
  }

  const base = data.leads.filter((r) => f.tests || !r.is_test);
  const visible = base.filter((r) => matches(r, f));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-leads-page">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-green-700">Audits Logement Wallonie</p>
            <h1 className="text-3xl font-bold text-slate-900">Leads & verdicts</h1>
            <p className="text-sm text-slate-500">Mis à jour le {formatDate(data.generated_at)}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => load(adminKey, f.days)} disabled={loading} data-testid="admin-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />Actualiser
            </Button>
            <Button variant="ghost" onClick={logout} data-testid="admin-logout-btn"><LogOut className="w-4 h-4 mr-2" />Se déconnecter</Button>
          </div>
        </div>
        <LeadsFilters f={f} set={set} />
        <SummaryCards rows={base} />
        <PagePerfTable data={data.pages} />
        <KeywordTable rows={base} onSelect={(k) => set({ keyword: k })} />
        <LeadsTable rows={visible} />
      </div>
    </div>
  );
}
